/**
 * Vote Service (web)
 *
 * Client for the Voting feature. Two service objects:
 *   - voteAdminService  → /api/vote/admin  (create/manage/results for admins)
 *   - voteUserService   → /api/votes       (member-facing list + cast ballot)
 *
 * Canonical TypeScript interfaces for the feature live here.
 */

import { apiRequest, API_BASE_URL, getCurrentUserToken } from '../config/api';
import { getActiveOrgId } from '../config/tenant';

// --- Types ---

export type QuestionType = 'single' | 'multiple' | 'yesno' | 'ranked';
export type ResultsVisibility = 'live' | 'after_close' | 'admin_only';
export type VoteStatus = 'draft' | 'scheduled' | 'open' | 'closed';
export type VoteLifecycle = 'draft' | 'published' | 'closed';

export interface VoteOption {
  id: string;
  label: string;
  imageUrl?: string;
}

export interface VoteQuestion {
  id: string;
  prompt: string;
  type: QuestionType;
  options: VoteOption[];
  maxSelections?: number;
}

export interface VoteScope {
  entityId: string;
  entityName: string;
  entityType: string;
}

export interface Vote {
  id: string;
  organizationId: string;
  title: string;
  description: string;
  scope: VoteScope;
  questions: VoteQuestion[];
  anonymous: boolean;
  resultsVisibility: ResultsVisibility;
  allowChange: boolean;
  quorum: number | null;
  opensAt: string | null;
  closesAt: string | null;
  lifecycle: VoteLifecycle;
  status: VoteStatus;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
  closedAt?: string | null;
  // annotations (context-dependent)
  totalBallots?: number;
  quorumMet?: boolean | null;
  hasVoted?: boolean;
  resultsVisible?: boolean;
  myBallot?: Ballot | null;
}

export interface BallotResponse {
  questionId: string;
  selectedOptionIds?: string[];
  ranking?: string[];
}

export interface Ballot {
  id: string;
  voteId: string;
  memberId: string;
  voterEntityId: string | null;
  responses: BallotResponse[];
  castAt: string;
}

export interface OptionResult {
  optionId: string;
  label: string;
  imageUrl?: string;
  count: number;
  percent: number;
  voters?: Array<{ memberId: string; name: string | null }>;
}

export interface RankedRound {
  totalActive: number;
  tallies: Array<{ optionId: string; label: string; count: number }>;
}

export interface QuestionResult {
  questionId: string;
  prompt: string;
  type: QuestionType;
  options?: VoteOption[];
  totalResponses: number;
  results?: OptionResult[];
  ranked?: {
    winner: { optionId: string; label: string } | null;
    rounds: RankedRound[];
  };
}

export interface VoteResults {
  voteId: string;
  title: string;
  status: VoteStatus;
  anonymous: boolean;
  quorum: number | null;
  totalBallots: number;
  quorumMet: boolean | null;
  resultsVisibility: ResultsVisibility;
  questions: QuestionResult[];
}

export interface CreateVotePayload {
  title: string;
  description?: string;
  scope: VoteScope;
  questions: Array<{
    prompt: string;
    type: QuestionType;
    options?: Array<{ label: string; imageUrl?: string }>;
    maxSelections?: number;
  }>;
  anonymous?: boolean;
  resultsVisibility?: ResultsVisibility;
  allowChange?: boolean;
  quorum?: number | null;
  opensAt?: string | null;
  closesAt?: string | null;
  publishNow?: boolean;
}

export type UpdateVotePayload = Partial<CreateVotePayload>;

// --- Admin service ---

class VoteAdminService {
  private basePath = '/api/vote/admin';

  async listVotes(filters?: { entityId?: string; status?: string }): Promise<Vote[]> {
    const params = new URLSearchParams();
    if (filters?.entityId) params.append('entityId', filters.entityId);
    if (filters?.status) params.append('status', filters.status);
    const query = params.toString();
    const url = query ? `${this.basePath}/votes?${query}` : `${this.basePath}/votes`;
    const res = await apiRequest<{ success: boolean; data: Vote[] }>(url);
    return res.data;
  }

  async createVote(payload: CreateVotePayload): Promise<Vote> {
    const res = await apiRequest<{ success: boolean; data: Vote }>(
      `${this.basePath}/votes`,
      { method: 'POST', body: JSON.stringify(payload) },
    );
    return res.data;
  }

  async getVote(id: string): Promise<Vote> {
    const res = await apiRequest<{ success: boolean; data: Vote }>(`${this.basePath}/votes/${id}`);
    return res.data;
  }

  async updateVote(id: string, payload: UpdateVotePayload): Promise<Vote> {
    const res = await apiRequest<{ success: boolean; data: Vote }>(
      `${this.basePath}/votes/${id}`,
      { method: 'PATCH', body: JSON.stringify(payload) },
    );
    return res.data;
  }

  async deleteVote(id: string): Promise<void> {
    await apiRequest(`${this.basePath}/votes/${id}`, { method: 'DELETE' });
  }

  async openVote(id: string): Promise<Vote> {
    const res = await apiRequest<{ success: boolean; data: Vote }>(
      `${this.basePath}/votes/${id}/open`, { method: 'POST' },
    );
    return res.data;
  }

  async closeVote(id: string): Promise<Vote> {
    const res = await apiRequest<{ success: boolean; data: Vote }>(
      `${this.basePath}/votes/${id}/close`, { method: 'POST' },
    );
    return res.data;
  }

  async getResults(id: string): Promise<VoteResults> {
    const res = await apiRequest<{ success: boolean; data: VoteResults }>(
      `${this.basePath}/votes/${id}/results`,
    );
    return res.data;
  }

  /** Upload an option/candidate image; returns its public URL. */
  async uploadOptionImage(file: File): Promise<string> {
    const token = await getCurrentUserToken();
    const form = new FormData();
    form.append('image', file);
    const res = await fetch(`${API_BASE_URL}${this.basePath}/upload-image`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'x-organization-id': getActiveOrgId(),
      },
      body: form,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Image upload failed (${res.status})`);
    }
    const json = await res.json();
    return json.data.url;
  }
}

// --- Member-facing service ---

class VoteUserService {
  private basePath = '/api/votes';

  async listVotes(): Promise<Vote[]> {
    const res = await apiRequest<{ success: boolean; data: Vote[] }>(this.basePath);
    return res.data;
  }

  async getVote(id: string): Promise<Vote> {
    const res = await apiRequest<{ success: boolean; data: Vote }>(`${this.basePath}/${id}`);
    return res.data;
  }

  async getResults(id: string): Promise<VoteResults> {
    const res = await apiRequest<{ success: boolean; data: VoteResults }>(
      `${this.basePath}/${id}/results`,
    );
    return res.data;
  }

  async getMyBallot(id: string): Promise<Ballot | null> {
    const res = await apiRequest<{ success: boolean; data: Ballot | null }>(
      `${this.basePath}/${id}/my-ballot`,
    );
    return res.data;
  }

  async castBallot(id: string, responses: BallotResponse[]): Promise<Ballot> {
    const res = await apiRequest<{ success: boolean; data: Ballot }>(
      `${this.basePath}/${id}/ballot`,
      { method: 'POST', body: JSON.stringify({ responses }) },
    );
    return res.data;
  }
}

export const voteAdminService = new VoteAdminService();
export const voteUserService = new VoteUserService();
